import { convertHtmlToPlainText } from './suneditor/convert_ck_contents';

// カテゴリテンプレートを「本文」ではなく「プレースホルダー」として表示する。
//
// 設計判断:
// - テンプレートは管理画面の SunEditor で入力された HTML だが、プレースホルダーは
//   プレーンテキストで表示する（改行は保持し、太字・リンク・画像などの書式は表示しない）。
// - SunEditor の内部 API（placeholder 要素の差し替え等）には依存しない。
//   SunEditor のメジャーアップデート（v2 → v3）で API が変わっても動くよう、
//   エディタの編集領域（contenteditable）の上に自前のオーバーレイ要素を重ねて表示する。
// - 素の textarea（Q&A / マークダウン / SunEditor 無効時のフォールバック）は placeholder 属性を差し替える。
//   元の placeholder は初回に退避し、プレースホルダー表示を解除したときに復元する。

// SunEditor 生成完了の通知。init.ts が dispatch している (post_body_dirty_tracker と同じ文字列)。
const EDITOR_READY_EVENT = 'suneditor:ready';

// 本文欄の候補。post_body_dirty_tracker の BODY_SELECTOR と揃える。
const BODY_SELECTOR = '.js-category-template-body, textarea[data-editor-id]';

const DEFAULT_PLACEHOLDER_ATTR = 'data-default-placeholder';
const OVERLAY_CLASS = 'js-category-template-placeholder';
const ACTIVE_ROOT_CLASS = 'is-category-template-placeholder';

// 本文として意味を持つ要素。テキストが空でもこれらがあれば「入力あり」として扱う
const CONTENT_ELEMENT_SELECTOR = 'img, iframe, video, audio, table, hr';

export function templateToPlaceholderText(html) {
  return convertHtmlToPlainText(html ?? '').trim();
}

function isEditableEmpty(editable) {
  const text = (editable.textContent ?? '').replace(/\u200B/g, '').trim();
  return text === '' && !editable.querySelector(CONTENT_ELEMENT_SELECTOR);
}

// SunEditor は下敷きの textarea の直後にエディタ本体を挿入する
function findEditorRoot(textarea) {
  const next = textarea.nextElementSibling;
  if (next?.classList.contains('sun-editor')) return next;
  return textarea.parentElement?.querySelector('.sun-editor') ?? null;
}

class CategoryTemplatePlaceholder {
  #container;
  #text = null;
  #observers = new Map();

  constructor(container = document) {
    this.#container = container;
    // エディタが後から生成された場合（CategoryTemplate より後に初期化されるページ）にも追従する
    document.addEventListener(EDITOR_READY_EVENT, () => this.#render());
  }

  get isActive() {
    return this.#text !== null;
  }

  // テンプレート HTML をプレースホルダーとして表示する
  show(templateHtml) {
    const text = templateToPlaceholderText(templateHtml);
    this.#text = text === '' ? null : text;
    this.#render();
  }

  // プレースホルダー表示を解除し、各本文欄の元の placeholder に戻す
  clear() {
    this.#text = null;
    this.#render();
  }

  #bodyElements() {
    return Array.from(this.#container.querySelectorAll(BODY_SELECTOR));
  }

  #render() {
    this.#bodyElements().forEach((el) => {
      this.#renderTextarea(el);
      if (el.matches('textarea[data-editor-id]')) this.#renderRichEditor(el);
    });
  }

  #renderTextarea(textarea) {
    if (!textarea.hasAttribute(DEFAULT_PLACEHOLDER_ATTR)) {
      textarea.setAttribute(
        DEFAULT_PLACEHOLDER_ATTR,
        textarea.getAttribute('placeholder') ?? '',
      );
    }
    textarea.setAttribute(
      'placeholder',
      this.#text ?? textarea.getAttribute(DEFAULT_PLACEHOLDER_ATTR),
    );
  }

  #renderRichEditor(textarea) {
    const root = findEditorRoot(textarea);
    const editable = root?.querySelector('[contenteditable="true"]');
    if (!root || !editable) return;

    let overlay = root.querySelector(`.${OVERLAY_CLASS}`);
    if (!this.#text) {
      overlay?.remove();
      root.classList.remove(ACTIVE_ROOT_CLASS);
      this.#observers.get(editable)?.disconnect();
      this.#observers.delete(editable);
      return;
    }

    if (!overlay) {
      overlay = document.createElement('div');
      // se-placeholder を併記し、SunEditor 標準のプレースホルダーと同じ見た目（色・余白）に揃える
      overlay.className = `se-placeholder ${OVERLAY_CLASS}`;
      overlay.setAttribute('aria-hidden', 'true');
      editable.parentElement.appendChild(overlay);
    }
    overlay.textContent = this.#text;
    // SunEditor 標準のプレースホルダーは重なるため隠す（CSS 側で制御）
    root.classList.add(ACTIVE_ROOT_CLASS);

    const update = () => {
      // MutationObserver のコールバックとしても使われるため、
      // clear() で要素が削除された場合に備えて毎回 DOM から取得し直す
      const current = root.querySelector(`.${OVERLAY_CLASS}`);
      if (current) {
        current.style.display = isEditableEmpty(editable) ? 'block' : 'none';
      }
    };
    update();

    // 入力・画像挿入・プログラムからの setContents のいずれでも表示を更新する
    if (!this.#observers.has(editable)) {
      const observer = new MutationObserver(update);
      observer.observe(editable, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      this.#observers.set(editable, observer);
    }
  }
}

export { CategoryTemplatePlaceholder };
